'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import {
  BookOpen,
  Shield,
  Clock,
  Award,
  Eye,
  EyeOff,
  AlertCircle,
  ChevronRight,
  Loader2,
  Lock,
  User,
} from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import { createClient } from '@/lib/supabase/client';

interface LoginFormValues {
  fullName: string;
  accessNumber: string;
}

const FEATURES = [
  {
    icon: Shield,
    title: 'Secure & Proctored',
    desc: 'AI-powered monitoring with real-time webcam analysis',
  },
  {
    icon: Clock,
    title: 'Timed Assessments',
    desc: 'Countdown timers with automatic submission on expiry',
  },
  {
    icon: BookOpen,
    title: 'Multiple Question Types',
    desc: 'MCQ, True/False, Short Answer, and Essay formats',
  },
  {
    icon: Award,
    title: 'Instant Results',
    desc: 'Auto-graded objective questions with score breakdown',
  },
];

export default function StudentLoginScreen() {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const [showAccessNumber, setShowAccessNumber] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormValues>({ mode: 'onBlur' });

  const onSubmit = async (data: LoginFormValues) => {
    setIsLoading(true);
    setLoginError(null);
    try {
      const supabase = createClient();
      // Look up student by full_name and access_number
      const { data: student, error } = await supabase
        .from('students')
        .select('id, full_name, access_number')
        .eq('full_name', data.fullName.trim())
        .eq('access_number', data.accessNumber.trim().toUpperCase())
        .maybeSingle();

      if (error || !student) {
        setLoginError('Invalid credentials. Please check your name and access number.');
        setIsLoading(false);
        return;
      }

      // Store student session in localStorage for exam taking
      if (typeof window !== 'undefined') {
        localStorage.setItem('student_session', JSON.stringify({ id: student.id, fullName: student.full_name, accessNumber: student.access_number }));
      }
      router.push('/student-dashboard');
    } catch (err: any) {
      setLoginError(err.message || 'An unexpected error occurred.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ fontFamily: 'var(--font-sans)' }}>
      {/* LEFT PANEL — Branding */}
      <div className="hidden lg:flex lg:w-[52%] xl:w-[55%] relative flex-col justify-between p-10 xl:p-14 overflow-hidden"
        style={{ background: 'linear-gradient(145deg, #1e1b4b 0%, #2e1065 40%, #1a0533 100%)' }}>
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full opacity-10"
            style={{ background: 'radial-gradient(circle, #7c3aed 0%, transparent 70%)' }} />
          <div className="absolute bottom-0 -left-16 w-80 h-80 rounded-full opacity-10"
            style={{ background: 'radial-gradient(circle, #a78bfa 0%, transparent 70%)' }} />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full opacity-5"
            style={{ background: 'radial-gradient(circle, #6d28d9 0%, transparent 60%)' }} />
          {/* Grid pattern */}
          <svg className="absolute inset-0 w-full h-full opacity-5" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="1"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>

        {/* Logo */}
        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <AppLogo size={44} />
            <span className="text-white text-2xl font-semibold tracking-tight">ExamPlatform</span>
          </div>
        </div>

        {/* Hero content */}
        <div className="relative z-10 flex-1 flex flex-col justify-center py-12">
          <div className="mb-3 inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium"
            style={{ background: 'rgba(124,58,237,0.25)', color: '#c4b5fd' }}>
            <span className="w-1.5 h-1.5 rounded-full bg-violet-400" />
            Secure Examination Environment
          </div>
          <h1 className="text-4xl xl:text-5xl font-bold text-white leading-tight mb-4">
            Your exam is
            <br />
            <span className="text-gradient-primary">ready for you</span>
          </h1>
          <p className="text-lg leading-relaxed mb-10" style={{ color: '#a5b4fc' }}>
            Enter your credentials to access your scheduled examination. Ensure you have a stable internet connection before starting.
          </p>

          <div className="grid grid-cols-1 gap-4">
            {FEATURES.map((f) => (
              <div key={`feature-${f.title}`}
                className="flex items-start gap-3 p-3 rounded-xl"
                style={{ background: 'rgba(255,255,255,0.05)' }}>
                <div className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center"
                  style={{ background: 'rgba(124,58,237,0.3)' }}>
                  <f.icon size={16} color="#c4b5fd" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">{f.title}</p>
                  <p className="text-xs mt-0.5" style={{ color: '#8b7fc7' }}>{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="relative z-10">
          <p className="text-xs" style={{ color: '#6b7280' }}>
            © 2026 ExamPlatform. All rights reserved. By logging in you agree to our{' '}
            <a href="#" className="underline" style={{ color: '#8b7fc7' }}>Terms of Service</a>
            {' '}and{' '}
            <a href="#" className="underline" style={{ color: '#8b7fc7' }}>Privacy Policy</a>.
          </p>
        </div>
      </div>

      {/* RIGHT PANEL — Login Form */}
      <div className="flex-1 flex flex-col justify-center items-center px-6 sm:px-10 xl:px-16 py-10"
        style={{ backgroundColor: '#f8f7ff' }}>
        {/* Mobile logo */}
        <div className="lg:hidden mb-8 flex items-center gap-2">
          <AppLogo size={40} />
          <span className="text-xl font-semibold" style={{ color: '#1e1b4b' }}>ExamPlatform</span>
        </div>

        <div className="w-full max-w-md">
          <div className="mb-8">
            <h2 className="text-2xl font-bold mb-1" style={{ color: '#1e1b4b' }}>
              Student Sign In
            </h2>
            <p className="text-sm" style={{ color: '#6b7280' }}>
              Enter your full name and access number to continue
            </p>
          </div>

          {/* Error Banner */}
          {loginError && (
            <div className="mb-5 flex items-start gap-3 p-3.5 rounded-xl border fade-in"
              style={{ background: '#fef2f2', borderColor: '#fecaca' }}>
              <AlertCircle size={18} color="#ef4444" className="flex-shrink-0 mt-0.5" />
              <p className="text-sm" style={{ color: '#dc2626' }}>{loginError}</p>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            {/* Full Name */}
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: '#374151' }}>
                Full Name
              </label>
              <p className="text-xs mb-2" style={{ color: '#9ca3af' }}>
                Enter your name exactly as registered by your teacher
              </p>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2">
                  <User size={16} color="#9ca3af" />
                </div>
                <input
                  {...register('fullName', {
                    required: 'Full name is required',
                    minLength: { value: 2, message: 'Name must be at least 2 characters' },
                  })}
                  type="text"
                  placeholder="e.g. John Mensah"
                  autoComplete="name"
                  className="exam-input pl-9"
                />
              </div>
              {errors.fullName && (
                <p className="mt-1.5 text-xs flex items-center gap-1" style={{ color: '#ef4444' }}>
                  <AlertCircle size={12} />
                  {errors.fullName.message}
                </p>
              )}
            </div>

            {/* Access Number */}
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: '#374151' }}>
                Access Number
              </label>
              <p className="text-xs mb-2" style={{ color: '#9ca3af' }}>
                Your unique exam access number — provided by your institution
              </p>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2">
                  <Lock size={16} color="#9ca3af" />
                </div>
                <input
                  {...register('accessNumber', {
                    required: 'Access number is required',
                    minLength: { value: 4, message: 'Access number must be at least 4 characters' },
                  })}
                  type={showAccessNumber ? 'text' : 'password'}
                  placeholder="e.g. CS2026-001"
                  autoComplete="off"
                  className="exam-input pl-9 pr-10"
                  style={{ fontFamily: 'var(--font-mono)', letterSpacing: '0.05em' }}
                />
                <button
                  type="button"
                  onClick={() => setShowAccessNumber((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded transition-colors"
                  style={{ color: '#9ca3af' }}
                  aria-label={showAccessNumber ? 'Hide access number' : 'Show access number'}
                >
                  {showAccessNumber ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {errors.accessNumber && (
                <p className="mt-1.5 text-xs flex items-center gap-1" style={{ color: '#ef4444' }}>
                  <AlertCircle size={12} />
                  {errors.accessNumber.message}
                </p>
              )}
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex items-center justify-center gap-2 py-3 px-6 rounded-xl font-semibold text-white transition-all btn-press disabled:opacity-60 disabled:cursor-not-allowed"
              style={{ background: isLoading ? '#4f46e5' : 'linear-gradient(135deg, #4f46e5 0%, #6d28d9 100%)' }}
            >
              {isLoading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Verifying credentials…
                </>
              ) : (
                <>
                  Sign In to Exam Portal
                  <ChevronRight size={18} />
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}